/**
 * nx-list — 목록 화면의 틀 하나 (사장님 지시 dxx145-3 · 2026-09-30). 마크업 = ui.listpage · 모양 = nx-list.css
 *
 * 하는 것 다섯
 *   ① 높이 — 목록 상자(.nx-list__body)가 도구줄 아래부터 페이징 위까지, **브라우저 세로에 고정**된다(통계 줄이 있으면 그만큼 줄어든다).
 *      문서 기준 위치로 잰다(화면 기준으로 재면 페이지를 내린 뒤 resize 때마다 자란다 — 2026-10-01 폰 실측 679→723px 로 잡은 결함).
 *      목록 화면에서는 레이아웃 푸터를 숨긴다(사장님 2026-10-01 ⓑ «페이징이 진짜 바닥») — body.nx-haslist 를 CSS 가 읽는다.
 *   ② 가로 스크롤 — 목록 상자는 뷰어 표 부품 `enableTableScroll` 로 **끌기 + 오른쪽 «더 있음» 화살표**(같은 종류는 같은 부품 — 0-0).
 *      도구줄은 끌지 않는다 — 얇은 막대만(지시 1.1). 도구줄에 끌기를 달면 검색칸·셀렉트의 클릭을 뺏는다.
 *   ③ 고정 열 — 기어 ⚙ 팝업에서 앞 1~3열·뒤 1~3열. **셀에는 아무것도 쓰지 않는다** — 표 뿌리에 CSS 변수 여섯(--nxl-1..3 · --nxr-1..3)과
 *      data-fix-lead/tail 만 적고, nth-child 규칙(nx-list.css)이 붙인다. 2026-10-01 검수: 셀마다 쓰던 판은 300줄에서 한 번 17ms ×
 *      resize 마다 두 번이었다. 지금은 머리 칸 폭 재기뿐이고, 행을 갈아 끼워도 다시 돌 일이 없다. 창 크기 변화는 rAF 로 한 프레임에 한 번.
 *      기본 앞 2 · 뒤 1 — 좁은 화면(640px 아래)은 앞 1 · 뒤 1(사장님 2026-09-30). 목록마다 localStorage `nx.list.<slot>.<id>` 에 기억.
 *      같은 팝업에 «한 쪽에 몇 줄»(서버 listing.PAGE_SIZES · 기본 50) — 쿠키 nx_n 으로 서버에 전한다(listing.per_page_of).
 *   ④ 팝업 — 틀 안의 `[data-nx-modal-open]`(행 링크 · 도구줄 단추)과 도구 셀렉트(`[data-nx-list-tool]`)를 nxModal 로 열고,
 *      서버 답(admin._done · support._back)대로 그 줄만 갈아 끼우거나(`<tr data-row-url>` · 스크롤 그대로) **목록만 다시 받거나**(refresh · 스크롤 그대로)
 *      화면을 다시 읽거나(reload) 같은 팝업의 **내용만** 다음 칸으로 바꾼다(keep_open+reopen · modal.goto — 닫았다 여는 게 아니다).
 *   ⑤ 거르개 셀렉트(`[data-nx-list-filter]`) — 고르면 그 폼을 바로 낸다(검색 단추를 또 누르지 않게 · 사장님 2026-10-01 «셀렉트로»).
 *   ⑥ 쪽 이동·거르개·검색·한 쪽 줄 수는 **fetch** 로 목록만 갈아 끼운다(loadList · pushState · 뒤로가기 popstate) — 사장님 2026-10-01 «fetch 로».
 *      카드 목록(cards=True · 나의 DOX · dx155-3)은 표 대신 `.nx-list__cards` 를 갈아 끼우고 `nx:list-swapped` 를 낸다.
 *      첫 화면은 그대로 서버가 그린다(주소로 북마크·공유 · JS 없이도 보인다). 못 받으면 그 주소로 보통 이동.
 *
 * ★ 좁은 화면에서 카드로 펼치지 않는다 — nx-rtable 을 안 붙였으니 rtable.ts 가 손대지 않는다.
 */
import { enableTableScroll } from './viewer_tablescroll';
import * as modal from './modal';

const MAX_FIX = 3;

interface FixCfg { lead: number; tail: number }

function slotKey(): string {
  return document.documentElement.dataset.slot || document.body.dataset.slot || 'slot';
}

function storeKey(id: string): string { return `nx.list.${slotKey()}.${id}`; }

/** 좁은 화면(폰) — 이 폭 아래면 기본 고정이 «앞 1 · 뒤 1» 이다(사장님 2026-09-30). 기어로 바꾼 값은 그대로 기억된다. */
export const NARROW_PX = 640;

function loadCfg(root: HTMLElement): FixCfg {
  const narrow = window.innerWidth < NARROW_PX;
  const dflt: FixCfg = {
    lead: narrow ? 1 : Math.min(MAX_FIX, Math.max(0, parseInt(root.dataset.lead || '2', 10) || 0)),
    tail: narrow ? 1 : Math.min(MAX_FIX, Math.max(0, parseInt(root.dataset.tail || '1', 10) || 0)),
  };
  try {
    const raw = localStorage.getItem(storeKey(root.dataset.nxList || ''));
    if (!raw) return dflt;
    const j = JSON.parse(raw) as Partial<FixCfg>;
    return {
      lead: Math.min(MAX_FIX, Math.max(0, Number(j.lead ?? dflt.lead))),
      tail: Math.min(MAX_FIX, Math.max(0, Number(j.tail ?? dflt.tail))),
    };
  } catch { return dflt; }
}

function saveCfg(root: HTMLElement, cfg: FixCfg): void {
  try { localStorage.setItem(storeKey(root.dataset.nxList || ''), JSON.stringify(cfg)); } catch { /* 사생활 모드 */ }
}

/** ③ 고정 열 — 머리 칸 폭만 재서 표 뿌리에 변수 여섯과 data-fix-* 를 적는다. 셀은 건드리지 않는다(CSS nth-child 가 붙인다). */
export function applyFixedColumns(root: HTMLElement, cfg: FixCfg): void {
  const table = root.querySelector<HTMLTableElement>('table.nx-ltable');
  if (!table) return;
  const head = table.tHead ? table.tHead.rows[0] : table.rows[0];
  if (!head) return;
  const n = head.cells.length;
  const lead = Math.min(cfg.lead, n);
  const tail = Math.min(cfg.tail, Math.max(0, n - lead));
  const widths = Array.from(head.cells).map((c) => c.getBoundingClientRect().width);
  let acc = 0;
  for (let i = 0; i < MAX_FIX; i++) {
    table.style.setProperty(`--nxl-${i + 1}`, `${acc}px`);
    acc += widths[i] || 0;
  }
  acc = 0;
  for (let i = 0; i < MAX_FIX; i++) {
    table.style.setProperty(`--nxr-${i + 1}`, `${acc}px`);
    acc += widths[n - 1 - i] || 0;
  }
  if (table.dataset.fixLead !== String(lead)) table.dataset.fixLead = String(lead);
  if (table.dataset.fixTail !== String(tail)) table.dataset.fixTail = String(tail);
}

/** ① 높이 — 목록 틀이 화면 아래까지 차게. 문서 기준(top + scrollY)이라 페이지를 내린 뒤 다시 재도 같은 값이다. */
function sizeList(root: HTMLElement): void {
  const docTop = root.getBoundingClientRect().top + window.scrollY;
  const bottomGap = 16;
  const h = Math.max(240, window.innerHeight - docTop - bottomGap);
  root.style.setProperty('--nx-list-h', `${h}px`);
}

/** 한 프레임에 한 번만 — resize 폭풍(초당 수십 번)에도 재기는 프레임당 1회. */
function coalesce(fn: () => void): () => void {
  let queued = false;
  return () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; fn(); });
  };
}

function setCookie(name: string, value: string): void {
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; samesite=lax${secure}`;
}

function wireGear(root: HTMLElement, cfg: FixCfg, redo: () => void): void {
  const btn = root.querySelector<HTMLButtonElement>('[data-nx-list-gear]');
  const pop = root.querySelector<HTMLElement>('[data-nx-list-gearpop]');
  if (!btn || !pop) return;
  const boxes = Array.from(pop.querySelectorAll<HTMLInputElement>('input[data-fix]'));
  const paint = () => {
    boxes.forEach((b) => {
      const n = parseInt(b.value, 10);
      b.checked = (b.dataset.fix === 'lead' ? cfg.lead : cfg.tail) >= n;
    });
  };
  paint();
  boxes.forEach((b) => b.addEventListener('change', () => {
    // n 번째를 켜면 그 앞도 켜지고, 끄면 그 뒤도 꺼진다 — «앞에서 n 열» 이라는 뜻을 지킨다.
    const n = parseInt(b.value, 10);
    const key = b.dataset.fix === 'lead' ? 'lead' : 'tail';
    cfg[key] = b.checked ? n : n - 1;
    paint(); saveCfg(root, cfg); redo();
  }));
  // 한 쪽에 몇 줄 — 쿠키 nx_n 에 적고 1쪽부터 다시 읽는다(서버 listing.per_page_of 가 읽는다). 모든 목록에 같이 적용.
  pop.querySelectorAll<HTMLInputElement>('input[data-per-page]').forEach((r) => r.addEventListener('change', () => {
    if (!r.checked) return;
    setCookie('nx_n', r.value);
    const u = new URL(window.location.href); u.searchParams.delete('page');
    void loadList(root, u.toString(), redo, { push: true });
  }));
  const close = () => { pop.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const open = pop.hidden;
    pop.hidden = !open; btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
  document.addEventListener('click', (e) => { if (!pop.hidden && !pop.contains(e.target as Node)) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
}

/** ④ 팝업 → 결과 반영. 서버 답(admin._done)에 따라
 *   {reload} → 목록 다시 읽기 · {url} → 그리로 · {reopen} → 행 갱신 뒤 같은 팝업 다시 열기 · 그 밖 → `<tr data-row-url>` 의 그 줄만 갱신. */
async function refreshRow(root: HTMLElement, tr: HTMLTableRowElement, redo: () => void): Promise<void> {
  const url = tr.dataset.rowUrl || '';
  if (!url) return;
  const scroll = root.querySelector<HTMLElement>('.nx-list__scroll');
  const keep = scroll ? { x: scroll.scrollLeft, y: scroll.scrollTop } : null;
  try {
    const res = await fetch(url, { credentials: 'same-origin', headers: { 'X-NX-Modal': '1' } });
    if (!res.ok) return;
    const tpl = document.createElement('template');
    tpl.innerHTML = (await res.text()).trim();
    const fresh = tpl.content.querySelector('tr');
    if (fresh) {
      tr.replaceWith(fresh);
      fresh.classList.add('nx-row--flash');
      setTimeout(() => fresh.classList.remove('nx-row--flash'), 1200);
      redo();                              // 글자가 길어져 칸 폭이 바뀌었을 수 있다
    }
    if (scroll && keep) { scroll.scrollLeft = keep.x; scroll.scrollTop = keep.y; }
  } catch { /* 못 받으면 그대로 둔다 — 새로고침이 정본 */ }
}

/** 목록을 **그 주소로** 다시 받는다 — 같은 틀만 갈아 끼운다(통계 줄·수·행·발). 화면·도구줄·기어·배선은 그대로.
 *  · refresh(접수·공지 새로 쓰기 뒤): 같은 주소 · 스크롤 그대로 — 사장님 2026-10-01 «리스트 업데이트(스크롤 유지)»
 *  · 쪽 이동·거르개·검색(사장님 2026-10-01 «fetch 로»): 그 주소 · pushState · 목록 스크롤은 맨 위. 뒤로가기(popstate)도 같은 길.
 *  못 받으면(네트워크·통짜 오류) 그 주소로 보통 이동한다 — 기능이 죽는 쪽으로 어긋나지 않는다. */
export async function loadList(root: HTMLElement, url: string, redo: () => void,
                               opts: { push?: boolean; keepScroll?: boolean } = {}): Promise<void> {
  const scroll = root.querySelector<HTMLElement>('.nx-list__scroll');
  const keep = scroll ? { x: scroll.scrollLeft, y: scroll.scrollTop } : null;
  root.classList.add('nx-list--busy');
  try {
    const res = await fetch(url, { credentials: 'same-origin', headers: { 'X-NX-List': '1' } });
    if (!res.ok) { window.location.assign(url); return; }
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.querySelector<HTMLElement>(`.nx-list[data-nx-list="${CSS.escape(root.dataset.nxList || '')}"]`);
    if (!fresh) { window.location.assign(url); return; }
    const swap = (sel: string) => {
      const a = root.querySelector<HTMLElement>(sel); const b = fresh.querySelector<HTMLElement>(sel);
      if (a && b) a.innerHTML = b.innerHTML;
    };
    swap('.nx-list__stats'); swap('.nx-list__count'); swap('tbody'); swap('.nx-list__cards'); swap('.nx-list__foot');
    // 도구줄은 갈아 끼우지 않는다(배선·초점이 산다) — 대신 거르개·검색칸의 **값**만 서버가 그린 대로 맞춘다(뒤로가기 때 어긋나지 않게).
    fresh.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.nx-list__toolscroll select[name], .nx-list__toolscroll input[name]').forEach((b) => {
      const a = root.querySelector<HTMLInputElement | HTMLSelectElement>(`.nx-list__toolscroll [name="${CSS.escape(b.name)}"]`);
      if (a && a.type !== 'hidden' && a.value !== b.value) a.value = b.value;
    });
    if (opts.push && url !== window.location.href) history.pushState({ nxList: root.dataset.nxList }, '', url);
    redo();
    // ★ dx155-3 — 갈아 끼운 뒤 알린다. 카드 목록(나의 DOX)은 카드마다 감시(진행원·변환·번역·검수)를 다시 건다(my_dox.html 이 받는다).
    root.dispatchEvent(new CustomEvent('nx:list-swapped', { bubbles: true }));
    if (scroll) {
      if (opts.keepScroll && keep) { scroll.scrollLeft = keep.x; scroll.scrollTop = keep.y; }
      else scroll.scrollTop = 0;
    }
  } catch { window.location.assign(url); }
  finally { root.classList.remove('nx-list--busy'); }
}

export function refreshList(root: HTMLElement, redo: () => void): Promise<void> {
  return loadList(root, window.location.href, redo, { keepScroll: true });
}

/** 쪽 이동(발의 링크) · 거르개·검색 폼(도구줄의 GET 폼) 을 fetch 로 — 주소는 똑같이 바뀐다(pushState · 북마크·뒤로가기 그대로). */
function wireNavigation(root: HTMLElement, redo: () => void): void {
  root.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('.nx-list__foot a[href]');
    if (!a || !root.contains(a) || a.target || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
    const u = new URL(a.href, window.location.href);
    if (u.origin !== window.location.origin) return;
    e.preventDefault();
    void loadList(root, u.toString(), redo, { push: true });
  });
  root.querySelectorAll<HTMLFormElement>('.nx-list__tools form').forEach((form) => {
    if ((form.method || 'get').toLowerCase() !== 'get') return;      // 일괄 실행(POST)은 그대로
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const u = new URL(form.action || window.location.href, window.location.href);
      u.search = '';
      new FormData(form).forEach((v, k) => { if (typeof v === 'string' && v !== '') u.searchParams.append(k, v); });
      void loadList(root, u.toString(), redo, { push: true });
    });
  });
  window.addEventListener('popstate', () => { void loadList(root, window.location.href, redo); });
}

/** 지금 떠 있는 목록 팝업 — 어느 목록·어느 행에서 열렸나(한 번에 하나). `reopen` 이 올 때 그 행을 갱신하고 같은 팝업의 내용만 바꾼다. */
let active: { root: HTMLElement; tr: HTMLTableRowElement | null; redo: () => void } | null = null;

function openFrom(root: HTMLElement, el: HTMLElement, url: string, tr: HTMLTableRowElement | null, redo: () => void): void {
  active = { root, tr, redo };
  modal.open(url, { size: (el.dataset.nxModalSize as 'sheet' | 'inset' | 'full') || 'inset',
                    title: el.dataset.nxModalTitle || undefined })
    .then(async (r) => {
      active = null;
      if (!r || typeof r !== 'object') return;           // 닫기·뒤로 — 아무 일 없음
      const d = r as { reload?: boolean; refresh?: boolean; url?: string };
      if (d.reload) { window.location.reload(); return; }
      if (d.refresh) { await refreshList(root, redo); return; }
      if (d.url) { window.location.assign(d.url); return; }
      if (tr) await refreshRow(root, tr, redo);
    }, () => { active = null; });
}

/** `{keep_open, reopen}` — 팝업을 **닫지 않고 내용만** 다음 주소로 바꾼다(modal.goto · 히스토리를 안 건드린다).
 *  ★ 닫았다 다시 열면 안 된다 — close 의 history.back() 이 비동기라 바로 이어 open 하면 pushState 가 삼켜지고,
 *    두 번째 닫기에 목록보다 한 칸 더 뒤로 간다(F796 과 같은 사고 — 2026-10-01 고객센터 인증번호 칸에서 실측: 두 번째 reopen 에 홈으로 갔다).
 *  행이 있으면(답글 단 문의 · 교체한 주문) 그 줄을 먼저 갈아 끼운다. */
document.addEventListener('nx:modal-keep', (e) => {
  const d = (e as CustomEvent).detail as { reopen?: string } | null;
  if (!d || !d.reopen || !active) return;
  const { root, tr, redo } = active;
  const go = () => { modal.goto(d.reopen as string); };
  if (tr) { void refreshRow(root, tr, redo).then(() => {
    active = { root, tr: root.querySelector<HTMLTableRowElement>(`tr[data-row-id="${CSS.escape(tr.dataset.rowId || '')}"]`), redo };
    go();
  }); } else go();
});

function wireRowPopups(root: HTMLElement, redo: () => void): void {
  root.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-nx-modal-open]');
    if (!el || !root.contains(el)) return;
    const url = el.dataset.nxModalOpen || el.getAttribute('href') || '';
    if (!url) return;
    e.preventDefault(); e.stopPropagation();
    openFrom(root, el, url, el.closest<HTMLTableRowElement>('tr[data-row-url]'), redo);
  }, true);
  // 도구 셀렉트 — 고르면 그 팝업을 열고 셀렉트는 머리글로 되돌린다(같은 도구를 또 고를 수 있게).
  root.querySelectorAll<HTMLSelectElement>('select[data-nx-list-tool]').forEach((sel) => {
    sel.addEventListener('change', () => {
      const url = sel.value;
      const opt = sel.options[sel.selectedIndex];
      sel.selectedIndex = 0;
      if (!url) return;
      if (opt && opt.dataset.nxPlain === '1') { window.location.assign(url); return; }   // 내려받기(txt) 같은 보통 링크
      openFrom(root, sel, url, null, redo);
    });
  });
}

/** ⑦ ↻ 새로 읽기(사장님 2026-10-03) — 지금 주소(검색·거르개·쪽)로 목록만 다시 받는다 · 목록 스크롤 그대로(refreshList 한 벌).
 *  고정 열·한 쪽 줄 수는 이 브라우저 설정(localStorage·쿠키 nx_n)이라 그대로 산다. 받는 동안 또 누르면 무시한다. */
function wireReload(root: HTMLElement, redo: () => void): void {
  const btn = root.querySelector<HTMLButtonElement>('[data-nx-list-reload]');
  if (!btn) return;
  btn.addEventListener('click', () => {
    if (root.classList.contains('nx-list--busy')) return;
    void refreshList(root, redo);
  });
}

/** ⑤ 거르개 셀렉트 — 고르면 폼을 바로 낸다. */
function wireFilters(root: HTMLElement): void {
  root.querySelectorAll<HTMLSelectElement>('select[data-nx-list-filter]').forEach((sel) => {
    sel.addEventListener('change', () => {
      const form = sel.form;
      if (!form) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.submit();
    });
  });
}

export function initList(): void {
  const lists = Array.from(document.querySelectorAll<HTMLElement>('.nx-list[data-nx-list]'));
  if (!lists.length) return;
  // ① 화면을 차지하는 목록(fill)이 하나라도 있으면 푸터를 숨긴다(nx-list.css). fill=False(nx-list--fit)는 높이를 재지 않는다.
  if (lists.some((l) => l.dataset.fill !== '0')) document.body.classList.add('nx-haslist');
  for (const root of lists) {
    const fit = root.dataset.fill === '0';
    const cfg = loadCfg(root);
    const redo = coalesce(() => applyFixedColumns(root, cfg));
    const relayout = coalesce(() => { if (!fit) sizeList(root); applyFixedColumns(root, cfg); });
    if (!fit) sizeList(root);
    enableTableScroll(root);               // ② 목록 상자(.nxe-tw)
    applyFixedColumns(root, cfg);
    wireGear(root, cfg, redo);
    wireRowPopups(root, redo);
    wireFilters(root);
    wireNavigation(root, redo);
    wireReload(root, redo);
    window.addEventListener('resize', relayout);
    if (typeof ResizeObserver === 'function') {
      // 칸 폭은 글꼴이 늦게 오거나 내용이 바뀔 때 변한다 — 표 자체를 본다(스크롤 상자가 아니라). rAF 로 묶여 프레임당 1회.
      const table = root.querySelector<HTMLElement>('table.nx-ltable');
      if (table) new ResizeObserver(redo).observe(table);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(redo).catch(() => {});
  }
}
